import { useEffect, useState, type ReactNode } from 'react';
import { eventName, eventPlace, sortEvents } from '@wr/shared';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ApiError, errorMessage, post } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, EmptyState, Notice } from '../../components/ui.js';
import type { Stage } from '../leads/data.js';
import { stageName, useStages } from '../leads/stages.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import {
  avatarColor,
  contactTodo,
  fullName,
  initialsOf,
  listContacts,
  listLeads,
  whenShort,
  type ContactRow,
  type LeadRow,
  type Page,
  type StatusFilter,
} from './data.js';

type Tab = 'contacts' | 'leads';

/**
 * People (brief section 6.5), as docs/prototype.html draws it: Contacts and
 * Leads tabs, Add contact and Add lead, search, event and status filters,
 * pages of 20. A contact opens its sheet over the list.
 */
export function PeopleList({ tab }: { tab: Tab }) {
  const { user, can, refresh } = useSignedIn();
  const { store, counts, eventId } = useOffline();
  const stages = useStages();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState<LocalRecord[]>([]);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [contacts, setContacts] = useState<Page<ContactRow> | null>(null);
  const [leads, setLeads] = useState<Page<LeadRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = params.get('q') ?? '';
  const status = (params.get('status') ?? 'all') as StatusFilter;
  const page = Number(params.get('page') ?? '1') || 1;
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  // Assign to event (Nov 3, 2026): an admin ticks records without an event.
  // Another tab or filter: nothing stays ticked.
  const filterKey = [tab, params.get('event'), from, to, q, status].join('|');
  const [ticked, setTicked] = useState<{ key: string; ids: Set<string> }>(() => ({
    key: filterKey,
    ids: new Set(),
  }));
  const picked = ticked.key === filterKey ? ticked.ids : new Set<string>();
  const setPicked = (next: Set<string> | ((was: Set<string>) => Set<string>)) =>
    setTicked({ key: filterKey, ids: typeof next === 'function' ? next(picked) : next });
  const [assigning, setAssigning] = useState(false);
  const [reload, setReload] = useState(0);
  const currentEvent = eventId ?? null;
  const event = params.get('event') ?? currentEvent ?? 'all';

  useEffect(() => {
    let current = true;
    void store?.listRecords('event').then((list) => current && setEvents(list));
    return () => {
      current = false;
    };
  }, [store, counts]);

  // The search waits for a pause in typing. A sheet opened meanwhile wins: its
  // address may not be rendered yet, so the search waits for the next render.
  useEffect(() => {
    if (search === q) return;
    const timer = setTimeout(() => {
      if (window.location.pathname === location.pathname) set('q', search);
    }, 300);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    let current = true;
    const query = { q, event, status, page, from, to };
    const load = tab === 'contacts' ? listContacts : listLeads;
    (load as (...a: Parameters<typeof listContacts>) => Promise<Page<ContactRow | LeadRow>>)(
      query,
      store,
      user.id,
    )
      .then((data) => {
        if (!current) return;
        setError(null);
        if (tab === 'contacts') setContacts(data as Page<ContactRow>);
        else setLeads(data as Page<LeadRow>);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
    // counts: a change saved or received refreshes the list.
  }, [tab, q, event, status, page, from, to, store, user.id, refresh, counts, reload]);

  function set(name: string, value: string, empty = '') {
    const next = new URLSearchParams(params);
    if (value === empty) next.delete(name);
    else next.set(name, value);
    if (name !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  const data = tab === 'contacts' ? contacts : leads;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const currentName = events.find((e) => e.id === currentEvent)?.event_code as string | undefined;
  const canAssign =
    event === 'none' &&
    !data?.offline &&
    (tab === 'contacts' ? can('contacts.update.all') : can('leads.update.all'));
  const toggle = (id: string) =>
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pageIds = data?.items.map((i) => i.id) ?? [];
  const allPicked = pageIds.length > 0 && pageIds.every((id) => picked.has(id));

  return (
    <>
      <div className="page-head">
        <h1 className="page">Contacts/Leads</h1>
        <div className="actions">
          {can('contacts.create') && (
            <Link className="btn sm" to="/contacts/new" state={{ background: location }}>
              Add contact
            </Link>
          )}
          {can('leads.create') && (
            <Link className="btn sm" to="/leads/new">
              Add lead
            </Link>
          )}
        </div>
      </div>
      <div className="seg" role="tablist" style={{ marginBottom: 12 }}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'contacts'}
          aria-pressed={tab === 'contacts'}
          onClick={() => navigate({ pathname: '/people', search: params.toString() })}
        >
          Contacts
          {tab === 'contacts' && contacts ? <span className="cnt"> {contacts.total}</span> : null}
        </button>
        {can('leads.read.own') || can('leads.read.all') ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'leads'}
            aria-pressed={tab === 'leads'}
            onClick={() => navigate({ pathname: '/people/leads', search: params.toString() })}
          >
            Leads{tab === 'leads' && leads ? <span className="cnt"> {leads.total}</span> : null}
          </button>
        ) : null}
      </div>
      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search name, company, email"
          aria-label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select aria-label="Event" value={event} onChange={(e) => set('event', e.target.value)}>
          {currentEvent && (
            <option value={currentEvent}>Current event: {currentName ?? 'this event'}</option>
          )}
          {events
            // A completed event shows when Events opened it (brief section 7.1).
            .filter((e) => e.id !== currentEvent && (e.status !== 'completed' || e.id === event))
            .map((e) => (
              <option key={e.id} value={e.id}>
                {String(e.event_code)}
              </option>
            ))}
          <option value="none">No event</option>
          <option value="past">Past events</option>
          <option value="all">All events</option>
        </select>
        <select
          aria-label="Status"
          value={status}
          onChange={(e) => set('status', e.target.value, 'all')}
        >
          <option value="all">Any status</option>
          <option value="todo">To document</option>
          <option value="synced">In HubSpot</option>
          <option value="mine">Only mine</option>
        </select>
        {event === 'none' && (
          <>
            <input
              type="date"
              aria-label="Created from"
              title="Created from"
              value={from}
              onChange={(e) => set('from', e.target.value)}
            />
            <input
              type="date"
              aria-label="Created to"
              title="Created to"
              value={to}
              onChange={(e) => set('to', e.target.value)}
            />
          </>
        )}
      </div>
      {canAssign && data && data.items.length > 0 && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={allPicked}
            onChange={() =>
              setPicked((was) => {
                const next = new Set(was);
                for (const id of pageIds) {
                  if (allPicked) next.delete(id);
                  else next.add(id);
                }
                return next;
              })
            }
          />
          Select every {tab === 'contacts' ? 'contact' : 'lead'} of this page
        </label>
      )}
      {data?.offline && (
        <Notice kind="info">
          No connection: this is what this device keeps (your records and the current
          event&rsquo;s).
        </Notice>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      {data && data.items.length === 0 && (
        <EmptyState title={tab === 'contacts' ? 'No contact found.' : 'No lead found.'}>
          Change the search or the filters.
        </EmptyState>
      )}
      {tab === 'contacts' && contacts && contacts.items.length > 0 && (
        <div className="list" aria-label="Contacts">
          {contacts.items.map((c) => {
            const item = (
              <ContactItem
                key={c.id}
                contact={c}
                onOpen={() =>
                  navigate({
                    pathname: `/people/contacts/${c.id}${c.to_check > 0 ? '/verify' : ''}`,
                    search: params.toString(),
                  })
                }
              />
            );
            return canAssign ? (
              <PickRow
                key={c.id}
                label={fullName(c) || c.email || 'contact'}
                checked={picked.has(c.id)}
                onToggle={() => toggle(c.id)}
              >
                {item}
              </PickRow>
            ) : (
              item
            );
          })}
        </div>
      )}
      {tab === 'leads' && leads && leads.items.length > 0 && (
        <div className="list" aria-label="Leads">
          {leads.items.map((l) => {
            const item = (
              <LeadItem
                key={l.id}
                lead={l}
                stages={stages}
                onOpen={() =>
                  navigate({ pathname: `/people/leads/${l.id}`, search: params.toString() })
                }
              />
            );
            return canAssign ? (
              <PickRow
                key={l.id}
                label={l.deal_name || l.company_name || 'lead'}
                checked={picked.has(l.id)}
                onToggle={() => toggle(l.id)}
              >
                {item}
              </PickRow>
            ) : (
              item
            );
          })}
        </div>
      )}
      {data && pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="btn sm"
            disabled={page <= 1}
            onClick={() => set('page', String(page - 1))}
          >
            Previous
          </button>
          <span>
            {page} / {pages}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={page >= pages}
            onClick={() => set('page', String(page + 1))}
          >
            Next
          </button>
        </nav>
      )}
      {canAssign && picked.size > 0 && (
        <div className="selbar" role="region" aria-label="Selection">
          <span>{picked.size} selected</span>
          <button type="button" className="btn sm" onClick={() => setPicked(new Set())}>
            Clear
          </button>
          <button type="button" className="btn sm primary" onClick={() => setAssigning(true)}>
            Assign to event
          </button>
        </div>
      )}
      {assigning && (
        <AssignToEvent
          tab={tab}
          ids={[...picked]}
          events={events}
          onClose={() => setAssigning(false)}
          onDone={() => {
            setAssigning(false);
            setPicked(new Set());
            setReload((n) => n + 1);
          }}
        />
      )}
      <Outlet />
    </>
  );
}

/** A row with its box to tick (Assign to event). */
function PickRow({
  label,
  checked,
  onToggle,
  children,
}: {
  label: string;
  checked: boolean;
  onToggle(): void;
  children: ReactNode;
}) {
  return (
    <div className="pick-row">
      <label>
        <input
          type="checkbox"
          aria-label={`Select ${label}`}
          checked={checked}
          onChange={onToggle}
        />
      </label>
      {children}
    </div>
  );
}

/**
 * Assign to event (decision of Nov 3, 2026): the event of records captured
 * at a show without it. Only records without an event change; a contact's
 * leads without one follow it, and a lead's contacts.
 */
function AssignToEvent({
  tab,
  ids,
  events,
  onClose,
  onDone,
}: {
  tab: Tab;
  ids: string[];
  events: LocalRecord[];
  onClose(): void;
  onDone(): void;
}) {
  const toast = useToast();
  const [chosen, setChosen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const kind = tab === 'contacts' ? 'contact' : 'lead';
  const what = `${ids.length} ${kind}${ids.length === 1 ? '' : 's'}`;
  const list = sortEvents(events.filter((e) => !e.deleted));

  async function assign() {
    if (!chosen || saving) return;
    setSaving(true);
    setFailed(null);
    try {
      const done = await post<{ contacts: number; leads: number; eventCode: string }>(
        '/people/assign-event',
        { eventId: chosen, [tab === 'contacts' ? 'contactIds' : 'leadIds']: ids },
      );
      toast(
        `${done.contacts} contact${done.contacts === 1 ? '' : 's'} and ${done.leads} lead${
          done.leads === 1 ? '' : 's'
        } assigned to ${done.eventCode}. They go to HubSpot by themselves.`,
      );
      onDone();
    } catch (err) {
      setFailed(errorMessage(err));
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Assign to event"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!chosen || saving}
            onClick={() => void assign()}
          >
            Assign {what}
          </button>
        </>
      }
    >
      <p className="note">
        {what} and their {tab === 'contacts' ? 'leads' : 'contacts'} without an event go on the
        event you choose. An event already set is never replaced. In HubSpot, Event ID and Last seen
        are filled only where they are empty.
      </p>
      <div className="evlist">
        {list.map((e) => (
          <button
            key={e.id}
            type="button"
            aria-pressed={e.id === chosen}
            onClick={() => setChosen(e.id)}
          >
            <span>
              <b className="evlist-name">{eventName(e)}</b>
              <br />
              <small className="note">{eventPlace(e)}</small>
            </span>
            {e.id === chosen && <span>Selected</span>}
          </button>
        ))}
      </div>
      {failed && <Notice kind="error">{failed}</Notice>}
    </Dialog>
  );
}

function ContactItem({ contact: c, onOpen }: { contact: ContactRow; onOpen(): void }) {
  const reading = c.reading_status === 'waiting' || c.reading_status === 'reading';
  const name = fullName(c) || (reading ? 'Reading the card…' : c.email || 'No name yet');
  return (
    <button type="button" className="row" onClick={onOpen}>
      <span
        className="av"
        style={{ background: avatarColor(name), color: '#fff' }}
        aria-hidden="true"
      >
        {initialsOf(c)}
      </span>
      <span className="tx">
        <span className="t1">{name}</span>
        <span className="t2">{[c.job_title, c.company].filter(Boolean).join(', ') || ' '}</span>
        <span className="le-tags">
          {reading && <span className="tag">Reading</span>}
          {c.reading_status === 'failed' && <span className="tag warn">Could not read</span>}
          {c.to_check > 0 && <span className="tag warn">{c.to_check} to check</span>}
          {!reading && c.reading_status !== 'failed' && contactTodo(c) && c.to_check === 0 && (
            <span className="tag warn">To document</span>
          )}
          <span className="tag">
            {c.lead_count > 0
              ? `${c.lead_count} lead${c.lead_count === 1 ? '' : 's'}`
              : 'Marketing contact'}
          </span>
        </span>
      </span>
      <span className="rt">
        {c.hubspot_status === 'synced' && (
          <span className="hs-dot" title="In HubSpot">
            HS
          </span>
        )}
        <span>{whenShort(c.created_at)}</span>
      </span>
    </button>
  );
}

const QUALIFICATION: Record<string, string> = {
  qualified: 'Qualified',
  disqualified: 'Disqualified',
};

/** A lead row: its contacts, Qualified, Disqualified or To document, its stage. */
function LeadItem({ lead: l, stages, onOpen }: { lead: LeadRow; stages: Stage[]; onOpen(): void }) {
  const title = l.deal_name || [l.company_name, l.event_code].filter(Boolean).join(' - ') || 'Lead';
  const todo = l.to_document && l.hubspot_status !== 'synced';
  return (
    <button type="button" className="row" onClick={onOpen}>
      <span className="av lead" aria-hidden="true">
        {initialsOf({ company: l.company_name ?? 'L' })}
      </span>
      <span className="tx">
        <span className="t1">{title}</span>
        <span className="t2">{l.contacts.join(', ') || 'No contact'}</span>
        <span className="le-tags">
          {l.qualification && <span className="tag">{QUALIFICATION[l.qualification]}</span>}
          {todo && <span className="tag warn">To document</span>}
          {l.stage_id && <span className="tag">{stageName(l.stage_id, stages)}</span>}
        </span>
      </span>
      <span className="rt">
        {l.hubspot_status === 'synced' && (
          <span className="hs-dot" title="In HubSpot">
            HS
          </span>
        )}
        <span>{whenShort(l.created_at)}</span>
      </span>
    </button>
  );
}
